import { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { 
  Users, Heart, Brain, Shield, AlertTriangle, Plus, Edit2, Trash2,
  Activity, Dna, TreeDeciduous, ClipboardList, CheckCircle, Info,
  User, UserCircle, Baby, Loader2, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const RELATIONSHIP_LABELS = {
  self: "Me",
  father: "Father",
  mother: "Mother",
  paternal_grandfather: "Paternal Grandfather",
  paternal_grandmother: "Paternal Grandmother",
  maternal_grandfather: "Maternal Grandfather",
  maternal_grandmother: "Maternal Grandmother",
  brother: "Brother",
  sister: "Sister",
  son: "Son",
  daughter: "Daughter",
  paternal_uncle: "Paternal Uncle",
  paternal_aunt: "Paternal Aunt",
  maternal_uncle: "Maternal Uncle",
  maternal_aunt: "Maternal Aunt"
};

const CATEGORY_COLORS = {
  cardiovascular: "from-red-500 to-rose-600",
  cancer: "from-purple-500 to-violet-600",
  metabolic: "from-orange-500 to-amber-600",
  neurological: "from-blue-500 to-cyan-600",
  autoimmune: "from-green-500 to-emerald-600",
  mental_health: "from-pink-500 to-fuchsia-600",
  other: "from-gray-500 to-slate-600"
};

const CATEGORY_ICONS = {
  cardiovascular: Heart,
  cancer: Shield,
  metabolic: Activity,
  neurological: Brain,
  autoimmune: Shield,
  mental_health: Brain,
  other: Activity
};

const RISK_COLORS = {
  low: "bg-green-500/20 text-green-400 border-green-500/30",
  moderate: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  very_high: "bg-red-500/20 text-red-400 border-red-500/30"
};

const GeneticHealthMap = ({ user, setUser }) => {
  const [activeTab, setActiveTab] = useState('tree');
  const [familyTree, setFamilyTree] = useState(null);
  const [conditions, setConditions] = useState({});
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [screenings, setScreenings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showEditMember, setShowEditMember] = useState(null);
  const [showConditionPicker, setShowConditionPicker] = useState(null);
  
  const [newMember, setNewMember] = useState({
    name: '',
    relationship: '',
    birth_year: '',
    is_alive: true,
    conditions: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [treeRes, conditionsRes] = await Promise.all([
        axios.get(`${API}/genetic-health/family-tree`),
        axios.get(`${API}/genetic-health/conditions`)
      ]);
      
      setFamilyTree(treeRes.data.family_tree);
      setConditions(conditionsRes.data.conditions);
      
      // Load risk assessment if we have family members
      if (treeRes.data.total_members > 0) {
        const [riskRes, screenRes] = await Promise.all([
          axios.get(`${API}/genetic-health/risk-assessment`),
          axios.get(`${API}/genetic-health/screening-recommendations`)
        ]);
        setRiskAssessment(riskRes.data);
        setScreenings(screenRes.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load genetic health data');
    } finally {
      setLoading(false);
    }
  };

  const quickSetup = async () => {
    try {
      await axios.post(`${API}/genetic-health/quick-setup`);
      toast.success('Family tree created! Now add health conditions.');
      loadData();
    } catch (error) {
      toast.error('Failed to create family tree');
    }
  };

  const addFamilyMember = async () => {
    if (!newMember.name || !newMember.relationship) {
      toast.error('Name and relationship are required');
      return;
    }
    
    try {
      const payload = {
        ...newMember,
        birth_year: newMember.birth_year ? parseInt(newMember.birth_year) : null
      };
      
      await axios.post(`${API}/genetic-health/family-member`, payload);
      toast.success('Family member added');
      setShowAddMember(false);
      setNewMember({ name: '', relationship: '', birth_year: '', is_alive: true, conditions: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const updateMemberConditions = async (memberId, conditions) => {
    try {
      await axios.put(`${API}/genetic-health/family-member/${memberId}`, { conditions });
      toast.success('Conditions updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update conditions');
    }
  };

  const deleteMember = async (memberId) => {
    if (!confirm('Remove this family member?')) return;
    
    try {
      await axios.delete(`${API}/genetic-health/family-member/${memberId}`);
      toast.success('Member removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const renderFamilyMember = (member, size = 'normal') => {
    if (!member) return null;
    
    const hasConditions = member.conditions?.length > 0;
    const sizeClasses = size === 'small' ? 'w-20 p-2' : 'w-32 p-3';
    
    return (
      <div 
        key={member.id}
        className={`${sizeClasses} bg-white/10 rounded-lg border border-white/20 hover:border-white/40 transition-colors cursor-pointer`}
        onClick={() => setShowConditionPicker(member)}
      >
        <div className="flex flex-col items-center text-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
            hasConditions ? 'bg-orange-500/30' : 'bg-teal-500/30'
          }`}>
            <User className={`w-5 h-5 ${hasConditions ? 'text-orange-400' : 'text-teal-400'}`} />
          </div>
          <span className="text-xs text-white font-medium truncate w-full">{member.name}</span>
          <span className="text-xs text-gray-400">{RELATIONSHIP_LABELS[member.relationship]}</span>
          {hasConditions && (
            <Badge className="mt-1 text-xs bg-orange-500/20 text-orange-300 border-orange-500/30">
              {member.conditions.length} condition{member.conditions.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Navigation user={user} setUser={setUser} />
        <div className="container mx-auto px-4 py-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation user={user} setUser={setUser} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600">
              <Dna className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">Family Genetic Health Map</h1>
          </div>
          <p className="text-gray-400">Track hereditary conditions and get personalized risk assessments</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/10 border-white/20">
            <TabsTrigger value="tree" className="data-[state=active]:bg-white/20">
              <TreeDeciduous className="w-4 h-4 mr-2" />
              Family Tree
            </TabsTrigger>
            <TabsTrigger value="risks" className="data-[state=active]:bg-white/20">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Risk Assessment
            </TabsTrigger>
            <TabsTrigger value="screenings" className="data-[state=active]:bg-white/20">
              <ClipboardList className="w-4 h-4 mr-2" />
              Screenings
            </TabsTrigger>
          </TabsList>

          {/* Family Tree Tab */}
          <TabsContent value="tree" className="space-y-6">
            {!familyTree || familyTree.total_members === 0 ? (
              <Card className="bg-white/10 border-white/20 text-white">
                <CardContent className="py-12 text-center">
                  <TreeDeciduous className="w-16 h-16 mx-auto text-gray-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Start Your Family Health Tree</h3>
                  <p className="text-gray-400 mb-6 max-w-md mx-auto">
                    Create a family tree and track hereditary conditions to get personalized risk assessments.
                  </p>
                  <div className="flex justify-center gap-4">
                    <Button
                      onClick={quickSetup}
                      className="bg-gradient-to-r from-purple-500 to-pink-600"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Quick Setup (7 Members)
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowAddMember(true)}
                      className="border-white/30 text-white hover:bg-white/10"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Manually
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Visual Family Tree */}
                <Card className="bg-white/10 border-white/20 text-white">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>Your Family Tree</CardTitle>
                        <CardDescription className="text-gray-400">
                          Click on a family member to add/edit their health conditions
                        </CardDescription>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setShowAddMember(true)}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Member
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-8">
                      {/* Grandparents Row */}
                      <div className="flex justify-center gap-16">
                        {/* Paternal Grandparents */}
                        <div className="text-center">
                          <p className="text-xs text-gray-500 mb-2">Paternal Side</p>
                          <div className="flex gap-2 justify-center">
                            {familyTree.grandparents?.paternal?.map(m => renderFamilyMember(m, 'small'))}
                          </div>
                        </div>
                        {/* Maternal Grandparents */}
                        <div className="text-center">
                          <p className="text-xs text-gray-500 mb-2">Maternal Side</p>
                          <div className="flex gap-2 justify-center">
                            {familyTree.grandparents?.maternal?.map(m => renderFamilyMember(m, 'small'))}
                          </div>
                        </div>
                      </div>

                      {/* Parents Row */}
                      <div className="flex justify-center gap-4">
                        {familyTree.parents?.map(m => renderFamilyMember(m))}
                      </div>

                      {/* Self & Siblings Row */}
                      <div className="flex justify-center items-center gap-4">
                        {familyTree.siblings?.filter(s => s.relationship === 'brother' || s.relationship === 'sister').slice(0, 2).map(m => renderFamilyMember(m, 'small'))}
                        {familyTree.self && (
                          <div className="relative">
                            {renderFamilyMember(familyTree.self)}
                            <Badge className="absolute -top-2 -right-2 bg-teal-500">You</Badge>
                          </div>
                        )}
                        {familyTree.siblings?.filter(s => s.relationship === 'brother' || s.relationship === 'sister').slice(2).map(m => renderFamilyMember(m, 'small'))}
                      </div>

                      {/* Children Row */}
                      {familyTree.children?.length > 0 && (
                        <div className="flex justify-center gap-2">
                          {familyTree.children.map(m => renderFamilyMember(m, 'small'))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Conditions Legend */}
                <Card className="bg-white/10 border-white/20 text-white">
                  <CardHeader>
                    <CardTitle className="text-lg">Tracked Conditions in Your Family</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {Object.entries(conditions).map(([category, condList]) => (
                        <div key={category} className="space-y-2">
                          <div className="flex items-center gap-2">
                            {(() => {
                              const Icon = CATEGORY_ICONS[category] || Activity;
                              return <Icon className="w-4 h-4 text-gray-400" />;
                            })()}
                            <span className="text-sm font-medium capitalize">{category.replace('_', ' ')}</span>
                          </div>
                          <div className="pl-6 space-y-1">
                            {condList.slice(0, 3).map(cond => (
                              <p key={cond.id} className="text-xs text-gray-400">{cond.name}</p>
                            ))}
                            {condList.length > 3 && (
                              <p className="text-xs text-gray-500">+{condList.length - 3} more</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Risk Assessment Tab */}
          <TabsContent value="risks" className="space-y-6">
            {!riskAssessment || riskAssessment.risks?.length === 0 ? (
              <Card className="bg-white/10 border-white/20 text-white">
                <CardContent className="py-12 text-center">
                  <AlertTriangle className="w-16 h-16 mx-auto text-gray-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Risk Data Yet</h3>
                  <p className="text-gray-400 mb-4">
                    Add health conditions to your family members to see personalized risk assessments.
                  </p>
                  <Button onClick={() => setActiveTab('tree')} variant="outline" className="border-white/30 text-white">
                    Go to Family Tree
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* High Risk Summary */}
                {riskAssessment.high_risk_conditions?.length > 0 && (
                  <Card className="bg-red-500/10 border-red-500/30 text-white">
                    <CardHeader>
                      <CardTitle className="text-red-400 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        High Priority Conditions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-4">
                        {riskAssessment.high_risk_conditions.map(risk => (
                          <div key={risk.condition_id} className="bg-white/5 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-medium">{risk.condition_name}</h4>
                              <Badge className={RISK_COLORS[risk.risk_level]}>
                                {risk.your_risk}% risk
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-400 mb-2">
                              Population average: {risk.population_risk}%
                            </p>
                            <Progress value={risk.your_risk} className="h-2 mb-2" />
                            <p className="text-xs text-gray-500">
                              Affected: {risk.affected_relatives.map(r => r.name).join(', ')}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* All Risks */}
                <Card className="bg-white/10 border-white/20 text-white">
                  <CardHeader>
                    <CardTitle>Complete Risk Assessment</CardTitle>
                    <CardDescription className="text-gray-400">
                      Based on conditions reported in {riskAssessment.family_members_count} family members
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {riskAssessment.risks.map(risk => (
                        <div key={risk.condition_id} className="bg-white/5 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-medium flex items-center gap-2">
                                {risk.condition_name}
                                <Badge variant="outline" className="text-xs capitalize">
                                  {risk.category.replace('_', ' ')}
                                </Badge>
                              </h4>
                              <p className="text-sm text-gray-400 mt-1">
                                Your risk: <span className="text-white font-medium">{risk.your_risk}%</span>
                                {' '}vs population average: {risk.population_risk}%
                                {' '}({risk.risk_multiplier}x higher)
                              </p>
                            </div>
                            <Badge className={RISK_COLORS[risk.risk_level]}>
                              {risk.risk_level.replace('_', ' ')}
                            </Badge>
                          </div>
                          
                          <Progress 
                            value={risk.your_risk} 
                            className="h-2 mb-3" 
                          />
                          
                          <div className="grid md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500 mb-1">Affected Family Members:</p>
                              <ul className="space-y-1">
                                {risk.affected_relatives.map((rel, i) => (
                                  <li key={`item-${i}`} className="text-gray-300">
                                    • {rel.name} ({RELATIONSHIP_LABELS[rel.relationship] || rel.relationship})
                                    {rel.onset_age && <span className="text-gray-500"> - age {rel.onset_age}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <p className="text-gray-500 mb-1">Recommended Screenings:</p>
                              <ul className="space-y-1">
                                {risk.recommended_screenings.slice(0, 3).map((s, i) => (
                                  <li key={`item-${i}`} className="text-teal-400">• {s}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Screenings Tab */}
          <TabsContent value="screenings" className="space-y-6">
            {!screenings ? (
              <Card className="bg-white/10 border-white/20 text-white">
                <CardContent className="py-12 text-center">
                  <ClipboardList className="w-16 h-16 mx-auto text-gray-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Screening Recommendations Yet</h3>
                  <p className="text-gray-400">
                    Add family health conditions to get personalized screening recommendations.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {screenings.urgent?.length > 0 && (
                  <Card className="bg-red-500/10 border-red-500/30 text-white">
                    <CardHeader>
                      <CardTitle className="text-red-400">🚨 Urgent - Schedule ASAP</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {screenings.urgent.map((item, i) => (
                        <div key={`item-${i}`} className="mb-4 last:mb-0">
                          <h4 className="font-medium">{item.condition} ({item.your_risk}% risk)</h4>
                          <ul className="mt-1 text-sm text-gray-300">
                            {item.screenings.map((s, j) => (
                              <li key={`screening-${j}`}>• {s}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {screenings.annual?.length > 0 && (
                  <Card className="bg-orange-500/10 border-orange-500/30 text-white">
                    <CardHeader>
                      <CardTitle className="text-orange-400">📅 Annual Screenings Recommended</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {screenings.annual.map((item, i) => (
                        <div key={`item-${i}`} className="mb-4 last:mb-0">
                          <h4 className="font-medium">{item.condition} ({item.your_risk}% risk)</h4>
                          <ul className="mt-1 text-sm text-gray-300">
                            {item.screenings.map((s, j) => (
                              <li key={`screening-${j}`}>• {s}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {screenings.periodic?.length > 0 && (
                  <Card className="bg-yellow-500/10 border-yellow-500/30 text-white">
                    <CardHeader>
                      <CardTitle className="text-yellow-400">📋 Periodic Screenings (Every 2-3 Years)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {screenings.periodic.map((item, i) => (
                        <div key={`item-${i}`} className="mb-4 last:mb-0">
                          <h4 className="font-medium">{item.condition} ({item.your_risk}% risk)</h4>
                          <ul className="mt-1 text-sm text-gray-300">
                            {item.screenings.map((s, j) => (
                              <li key={`screening-${j}`}>• {s}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {screenings.baseline?.length > 0 && (
                  <Card className="bg-white/10 border-white/20 text-white">
                    <CardHeader>
                      <CardTitle className="text-green-400">✓ Baseline Screenings</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {screenings.baseline.map((item, i) => (
                        <div key={`item-${i}`} className="mb-4 last:mb-0">
                          <h4 className="font-medium">{item.condition} ({item.your_risk}% risk)</h4>
                          <ul className="mt-1 text-sm text-gray-300">
                            {item.screenings.map((s, j) => (
                              <li key={`screening-${j}`}>• {s}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Member Dialog */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent className="bg-slate-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Add Family Member</DialogTitle>
            <DialogDescription className="text-gray-400">
              Add a family member to track their health conditions.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Name</Label>
              <Input
                value={newMember.name}
                onChange={e => setNewMember({...newMember, name: e.target.value})}
                placeholder="Enter name"
                className="bg-white/10 border-white/20"
              />
            </div>
            <div>
              <Label>Relationship</Label>
              <Select value={newMember.relationship} onValueChange={v => setNewMember({...newMember, relationship: v})}>
                <SelectTrigger className="bg-white/10 border-white/20">
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(RELATIONSHIP_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Birth Year (optional)</Label>
              <Input
                type="number"
                value={newMember.birth_year}
                onChange={e => setNewMember({...newMember, birth_year: e.target.value})}
                placeholder="e.g., 1960"
                className="bg-white/10 border-white/20"
              />
            </div>
            <Button onClick={addFamilyMember} className="w-full bg-teal-600 hover:bg-teal-700">
              Add Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Condition Picker Dialog */}
      <Dialog open={!!showConditionPicker} onOpenChange={() => setShowConditionPicker(null)}>
        <DialogContent className="bg-slate-900 border-white/20 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showConditionPicker?.name}'s Health Conditions
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Select conditions that {showConditionPicker?.name} has or had.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {Object.entries(conditions).map(([category, condList]) => (
              <div key={category}>
                <h4 className="font-medium capitalize mb-2 flex items-center gap-2">
                  {(() => {
                    const Icon = CATEGORY_ICONS[category] || Activity;
                    return <Icon className="w-4 h-4" />;
                  })()}
                  {category.replace('_', ' ')}
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {condList.map(cond => {
                    const isSelected = showConditionPicker?.conditions?.includes(cond.id);
                    return (
                      <button
                        key={cond.id}
                        onClick={() => {
                          const current = showConditionPicker?.conditions || [];
                          const updated = isSelected 
                            ? current.filter(c => c !== cond.id)
                            : [...current, cond.id];
                          updateMemberConditions(showConditionPicker.id, updated);
                          setShowConditionPicker({...showConditionPicker, conditions: updated});
                        }}
                        className={`p-2 rounded-lg text-left text-sm transition-colors ${
                          isSelected 
                            ? 'bg-teal-500/30 border border-teal-500/50' 
                            : 'bg-white/5 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {isSelected && <CheckCircle className="w-4 h-4 text-teal-400" />}
                          <span>{cond.name}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            
            <div className="flex justify-between pt-4 border-t border-white/10">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  deleteMember(showConditionPicker.id);
                  setShowConditionPicker(null);
                }}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Remove Member
              </Button>
              <Button onClick={() => setShowConditionPicker(null)}>
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GeneticHealthMap;
